import { useState, useEffect } from 'react'
import {
  CreditCard,
  Crown,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Sliders,
  Save,
  Plus,
  RefreshCw,
  DollarSign,
  FileText,
  Tag,
  ShieldCheck,
  Zap,
} from 'lucide-react'
import { Badge, Button, Card, CardContent, Input } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { useAdminSettingsQuery, useUpdateSettingsMutation, useAdminSubscriptions } from '../../hooks/use-admin'
import { api, unwrap } from '@/api'
import { toast } from 'sonner'

export function AdminBillingPage() {
  const { data: rawSettings } = useAdminSettingsQuery()
  const { data: subscriptionsData } = useAdminSubscriptions()
  const updateSettingsMutation = useUpdateSettingsMutation()

  // Dynamic Gateway Config State
  const [activeGateway, setActiveGateway] = useState<'RAZORPAY' | 'CASHFREE' | 'PHONEPE' | 'STRIPE'>('RAZORPAY')
  const [gatewayMode, setGatewayMode] = useState<'SANDBOX' | 'LIVE'>('SANDBOX')
  const [keyId, setKeyId] = useState('')
  const [keySecret, setKeySecret] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [gatewayEnabled, setGatewayEnabled] = useState(true)

  // SaaS Subscription Change Policy State
  const [upgradePolicy, setUpgradePolicy] = useState<'IMMEDIATE' | 'NEXT_BILLING_CYCLE'>('IMMEDIATE')
  const [downgradePolicy, setDowngradePolicy] = useState<'IMMEDIATE' | 'END_OF_BILLING_CYCLE'>('END_OF_BILLING_CYCLE')
  const [cancellationPolicy, setCancellationPolicy] = useState<'IMMEDIATE' | 'END_OF_CYCLE' | 'GRACE_PERIOD'>('END_OF_CYCLE')
  const [trialConversion, setTrialConversion] = useState<'AUTOMATIC' | 'MANUAL' | 'PAYMENT_REQUIRED'>('AUTOMATIC')

  // Lists & Overrides
  const [invoices, setInvoices] = useState<any[]>([])
  const [refunds, setRefunds] = useState<any[]>([])
  const [coupons, setCoupons] = useState<any[]>([])

  useEffect(() => {
    if (rawSettings && Array.isArray(rawSettings)) {
      const gwSetting = rawSettings.find((s: any) => s.key === 'PAYMENT_GATEWAY_CONFIG')
      if (gwSetting && gwSetting.value) {
        setActiveGateway(gwSetting.value.gateway || 'RAZORPAY')
        setGatewayMode(gwSetting.value.mode || 'SANDBOX')
        setKeyId(gwSetting.value.keyId || '')
        setKeySecret(gwSecretMask(gwSetting.value.keySecret))
        setWebhookSecret(gwSecretMask(gwSetting.value.webhookSecret))
        setGatewayEnabled(gwSetting.value.enabled ?? true)
      }

      const policySetting = rawSettings.find((s: any) => s.key === 'SUBSCRIPTION_CHANGE_POLICIES')
      if (policySetting && policySetting.value) {
        setUpgradePolicy(policySetting.value.upgradePolicy || 'IMMEDIATE')
        setDowngradePolicy(policySetting.value.downgradePolicy || 'END_OF_BILLING_CYCLE')
        setCancellationPolicy(policySetting.value.cancellationPolicy || 'END_OF_CYCLE')
        setTrialConversion(policySetting.value.trialConversion || 'AUTOMATIC')
      }
    }

    // Fetch invoices, refunds, coupons
    api.get('/platform/billing/admin/invoices').then((res) => setInvoices(unwrap(res) || []))
    api.get('/platform/billing/admin/refunds').then((res) => setRefunds(unwrap(res) || []))
    api.get('/platform/billing/admin/coupons').then((res) => setCoupons(unwrap(res) || []))
  }, [rawSettings])

  const gwSecretMask = (val?: string) => (val ? (val.includes('placeholder') ? val : '••••••••••••') : '')

  const handleSaveGatewayConfig = async () => {
    await updateSettingsMutation.mutateAsync([
      {
        key: 'PAYMENT_GATEWAY_CONFIG',
        value: {
          gateway: activeGateway,
          mode: gatewayMode,
          keyId,
          keySecret,
          webhookSecret,
          enabled: gatewayEnabled,
        },
      },
    ])
  }

  const handleSavePolicies = async () => {
    await updateSettingsMutation.mutateAsync([
      {
        key: 'SUBSCRIPTION_CHANGE_POLICIES',
        value: {
          upgradePolicy,
          downgradePolicy,
          cancellationPolicy,
          trialConversion,
        },
      },
    ])
  }

  const plans = subscriptionsData?.plans ?? []

  return (
    <div className="space-y-8 max-w-6xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeading
          eyebrow="Platform Governance & Billing"
          title="Billing & Payment Gateway Control Center"
          description="Configure payment gateways, environment keys, SaaS subscription change policies, refund processing, and coupon codes."
          align="left"
        />
      </div>

      {/* Metrics Row */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="border-border/80 bg-card p-4 space-y-1">
          <span className="text-xs text-muted-foreground font-semibold">Active Subscribers</span>
          <p className="font-display text-2xl font-extrabold text-foreground">12</p>
        </Card>
        <Card className="border-border/80 bg-card p-4 space-y-1">
          <span className="text-xs text-muted-foreground font-semibold">Trial Users</span>
          <p className="font-display text-2xl font-extrabold text-blue-500">5</p>
        </Card>
        <Card className="border-border/80 bg-card p-4 space-y-1">
          <span className="text-xs text-muted-foreground font-semibold">Past Due / Grace</span>
          <p className="font-display text-2xl font-extrabold text-amber-500">2</p>
        </Card>
        <Card className="border-border/80 bg-card p-4 space-y-1">
          <span className="text-xs text-muted-foreground font-semibold">Total Revenue (Gross)</span>
          <p className="font-display text-2xl font-extrabold text-emerald-500">₹45,990</p>
        </Card>
      </div>

      {/* 1. Dynamic Payment Gateway Settings */}
      <Card className="border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card backdrop-blur-xl">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between border-b border-border/60 pb-3">
            <span className="font-display text-base font-bold text-foreground flex items-center gap-2">
              <CreditCard className="size-5 text-primary" /> Dynamic Payment Gateway Configuration
            </span>
            <Button onClick={handleSaveGatewayConfig} disabled={updateSettingsMutation.isPending} className="gap-1.5 text-xs font-bold bg-primary">
              <Save className="size-3.5" /> Save Gateway Config
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 text-xs">
            <div className="space-y-1.5">
              <label className="font-bold text-foreground">Active Payment Gateway Provider</label>
              <select
                value={activeGateway}
                onChange={(e) => setActiveGateway(e.target.value as any)}
                className="w-full rounded-lg border border-border/80 bg-background px-3 py-2 text-xs font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="RAZORPAY">Razorpay (Active)</option>
                <option value="CASHFREE">Cashfree (Future Ready)</option>
                <option value="PHONEPE">PhonePe (Future Ready)</option>
                <option value="STRIPE">Stripe International (Future Ready)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="font-bold text-foreground">Gateway Environment Mode</label>
              <select
                value={gatewayMode}
                onChange={(e) => setGatewayMode(e.target.value as any)}
                className="w-full rounded-lg border border-border/80 bg-background px-3 py-2 text-xs font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="SANDBOX">Sandbox / Test Mode</option>
                <option value="LIVE">Live Production Mode</option>
              </select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3 text-xs">
            <div className="space-y-1">
              <label className="font-semibold text-muted-foreground">API Key / Key ID</label>
              <Input value={keyId} onChange={(e) => setKeyId(e.target.value)} placeholder="rzp_test_..." />
            </div>
            <div className="space-y-1">
              <label className="font-semibold text-muted-foreground">API Key Secret</label>
              <Input type="password" value={keySecret} onChange={(e) => setKeySecret(e.target.value)} placeholder="Secret..." />
            </div>
            <div className="space-y-1">
              <label className="font-semibold text-muted-foreground">Webhook Secret</label>
              <Input type="password" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} placeholder="Webhook secret..." />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2. SaaS Subscription Change Policies */}
      <Card className="border-purple-500/30 bg-gradient-to-br from-purple-500/5 via-card to-card backdrop-blur-xl">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between border-b border-border/60 pb-3">
            <span className="font-display text-base font-bold text-foreground flex items-center gap-2">
              <Sliders className="size-5 text-purple-500" /> SaaS Subscription Change Policies
            </span>
            <Button onClick={handleSavePolicies} disabled={updateSettingsMutation.isPending} className="gap-1.5 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white">
              <Save className="size-3.5" /> Save SaaS Policies
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 text-xs">
            <div className="space-y-1.5 p-3 rounded-xl border border-purple-500/20 bg-background">
              <label className="font-bold text-foreground">Upgrade Policy</label>
              <select
                value={upgradePolicy}
                onChange={(e) => setUpgradePolicy(e.target.value as any)}
                className="w-full rounded-lg border border-border/80 bg-background px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="IMMEDIATE">Immediate Activation (Prorated)</option>
                <option value="NEXT_BILLING_CYCLE">At Next Billing Cycle</option>
              </select>
            </div>

            <div className="space-y-1.5 p-3 rounded-xl border border-purple-500/20 bg-background">
              <label className="font-bold text-foreground">Downgrade Policy</label>
              <select
                value={downgradePolicy}
                onChange={(e) => setDowngradePolicy(e.target.value as any)}
                className="w-full rounded-lg border border-border/80 bg-background px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="END_OF_BILLING_CYCLE">At End of Billing Cycle</option>
                <option value="IMMEDIATE">Immediate Downgrade</option>
              </select>
            </div>

            <div className="space-y-1.5 p-3 rounded-xl border border-purple-500/20 bg-background">
              <label className="font-bold text-foreground">Cancellation Policy</label>
              <select
                value={cancellationPolicy}
                onChange={(e) => setCancellationPolicy(e.target.value as any)}
                className="w-full rounded-lg border border-border/80 bg-background px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="END_OF_CYCLE">Cancel at End of Cycle</option>
                <option value="IMMEDIATE">Immediate Cancellation</option>
                <option value="GRACE_PERIOD">Allow 3-Day Grace Period</option>
              </select>
            </div>

            <div className="space-y-1.5 p-3 rounded-xl border border-purple-500/20 bg-background">
              <label className="font-bold text-foreground">Trial Conversion</label>
              <select
                value={trialConversion}
                onChange={(e) => setTrialConversion(e.target.value as any)}
                className="w-full rounded-lg border border-border/80 bg-background px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="AUTOMATIC">Auto Convert if Payment Added</option>
                <option value="PAYMENT_REQUIRED">Require Payment Before Access</option>
                <option value="MANUAL">Manual Admin Activation</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
